import { useCallback, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";

function RippleButton({ children, className = "", disabled, ...props }) {
  const buttonRef = useRef(null);
  const [ripple, setRipple] = useState(null);
  const [isHovered, setIsHovered] = useState(false);

  const createRipple = useCallback((event) => {
    if (disabled || isHovered || !buttonRef.current) return;
    setIsHovered(true);

    const button = buttonRef.current;
    const rect = button.getBoundingClientRect();
    const size = Math.max(rect.width, rect.height) * 2;
    const x = event.clientX - rect.left;
    const y = event.clientY - rect.top;

    setRipple({ x, y, size, key: Date.now() });
  }, [isHovered, disabled]);

  const removeRipple = useCallback((event) => {
    if (event.target !== event.currentTarget) return;
    setIsHovered(false);

    const button = buttonRef.current;
    if (!button) return;
    const rect = button.getBoundingClientRect();
    const size = Math.max(rect.width, rect.height) * 2;
    const x = event.clientX - rect.left;
    const y = event.clientY - rect.top;

    setRipple({ x, y, size, key: Date.now(), isLeaving: true });
  }, []);

  const handleMouseMove = useCallback((event) => {
    if (!buttonRef.current || !isHovered || !ripple || disabled) return;

    const button = buttonRef.current;
    const rect = button.getBoundingClientRect();
    const x = event.clientX - rect.left;
    const y = event.clientY - rect.top;

    setRipple((prev) => ({ ...prev, x, y }));
  }, [isHovered, ripple, disabled]);

  return (
    <button
      ref={buttonRef}
      disabled={disabled}
      className={`relative flex items-center justify-center overflow-hidden rounded-xl shadow-lg border border-dashed border-blue-500/50 px-8 py-3 text-base font-medium text-blue-600 transition-colors duration-[600ms] hover:text-white hover:border-blue-600 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:text-blue-600 ${className}`}
      onMouseEnter={(e) => {
        if (e.target === e.currentTarget && !disabled) createRipple(e);
      }}
      onMouseLeave={(e) => {
        if (e.target === e.currentTarget) removeRipple(e);
      }}
      onMouseMove={handleMouseMove}
      {...props}
    >
      <span className="relative z-[2] flex items-center justify-center gap-2">
        {children}
      </span>

      <AnimatePresence>
        {ripple && !disabled && (
          <motion.span
            key={ripple.key}
            className="absolute rounded-full bg-blue-500 pointer-events-none z-[1]"
            style={{
              width: ripple.size,
              height: ripple.size,
              left: ripple.x,
              top: ripple.y,
              x: "-50%",
              y: "-50%",
            }}
            initial={{ scale: 0, opacity: 1 }}
            animate={{
              scale: ripple.isLeaving ? 0 : 1,
              x: "-50%",
              y: "-50%",
            }}
            exit={{ scale: 0, opacity: 1 }}
            transition={{
              duration: 0.6,
              ease: "easeOut",
            }}
            onAnimationComplete={() => {
              if (ripple.isLeaving) {
                setRipple(null);
              }
            }}
          />
        )}
      </AnimatePresence>
    </button>
  );
}

export default RippleButton;
